import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getTeacherProfileById } from "../../../services/TeacherProfileService";
import { getUserById, uploadUserAvatar } from "../../../services/UserService";
import { getDepartmentById } from "../../../services/DepartmentService";
import { getExams } from "../../../services/ExamService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getSubjects } from "../../../services/SubjectService";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../../utils/permissions";
import { getLocale, lazyLabels } from "../../../utils/i18nLabels";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(getLocale(), { day: "2-digit", month: "short", year: "numeric" })
    : "-";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(getLocale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "-";

const examStatusLabels = lazyLabels({ draft: "common.examStatus.draft", published: "common.examStatus.published", archived: "common.examStatus.archived" });
const examStatusBadge = { draft: "secondary", published: "success", archived: "warning" };

const sessionStatusLabels = lazyLabels({ scheduled: "common.examSessionStatus.scheduled", ongoing: "common.examSessionStatus.ongoing", closed: "common.examSessionStatus.closed", cancelled: "common.examSessionStatus.cancelled" });
const sessionStatusBadge = { scheduled: "info", ongoing: "primary", closed: "success", cancelled: "danger" };

const TeacherDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [examSessions, setExamSessions] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await getTeacherProfileById(id);
        const teacherProfile = res.data?.data;
        if (cancelled) return;
        setProfile(teacherProfile);

        const [userRes, departmentRes] = await Promise.all([
          teacherProfile?.user_id ? getUserById(teacherProfile.user_id).catch(() => null) : null,
          teacherProfile?.department_id ? getDepartmentById(teacherProfile.department_id).catch(() => null) : null,
        ]);
        if (cancelled) return;
        setAccount(userRes?.data?.data || null);
        setDepartment(departmentRes?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || t("pages.teacherDetail.impossibleChargerDetailsEnseignant"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!profile?.user_id) return undefined;
    let cancelled = false;

    const loadActivity = async () => {
      setActivityLoading(true);
      setActivityError("");
      try {
        const [subjectsRes, examsRes, sessionsRes] = await Promise.all([
          getSubjects({ page: 1, limit: 200 }),
          getExams({ page: 1, limit: 200 }),
          getExamSessions({ page: 1, limit: 200, sort_by: "start_at", sort_dir: "desc" }),
        ]);
        if (cancelled) return;
        setSubjects(subjectsRes.data?.data || []);

        const teacherExams = (examsRes.data?.data || []).filter((exam) => exam.created_by === profile.user_id);
        setExams(teacherExams);

        const teacherExamIds = new Set(teacherExams.map((exam) => exam.id));
        const teacherSessions = (sessionsRes.data?.data || []).filter((session) =>
          teacherExamIds.has(session.exam_id),
        );
        setExamSessions(teacherSessions);
      } catch (err) {
        if (!cancelled) {
          setActivityError(err?.response?.data?.message || t("pages.teacherDetail.impossibleChargerActiviteEnseignement"));
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };

    loadActivity();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const name = [account?.first_name, account?.last_name].filter(Boolean).join(" ") || account?.email || "-";
  const inactive = account?.is_active === false;

  // Photo de profil : seul un compte disposant de USER_UPDATE peut la
  // modifier depuis la fiche (mêmes règles que le back : permit('USER_UPDATE')
  // sur POST /users/:id/avatar).
  const canEditAvatar = usePermissions()("USER_UPDATE");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef(null);

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !account?.id) return;

    setAvatarError("");
    setAvatarUploading(true);
    try {
      const res = await uploadUserAvatar(account.id, file);
      const updated = res.data?.data;
      setAccount((prev) => ({
        ...prev,
        avatar: updated?.avatar,
        avatar_url: updated?.avatar_url,
      }));
    } catch (err) {
      setAvatarError(
        err?.response?.data?.message || t("header.avatar.error"),
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const subjectLabel = (subjectId) => subjects.find((s) => s.id === subjectId)?.name || "-";
  const examLabel = (examId) => exams.find((e) => e.id === examId)?.title || "-";

  const activityStats = useMemo(
    () => ({
      totalExams: exams.length,
      publishedExams: exams.filter((e) => e.status === "published").length,
      totalSessions: examSessions.length,
      ongoingSessions: examSessions.filter((s) => s.status === "ongoing").length,
    }),
    [exams, examSessions],
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/teacher">{t("menu.teachers.title")}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("pages.teacherDetail.breadcrumb")}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("common.crud.loading")}</span>
            </div>
          </div>
        ) : loadError ? (
          <div className="alert alert-danger">{loadError}</div>
        ) : !profile ? (
          <div className="alert alert-warning">{t("pages.teacherDetail.notFound")}</div>
        ) : (
          <div className="row">
            {/* Teacher Information */}
            <div className="col-xxl-4 col-xl-5 theiaStickySidebar">
              <div className="card border-white">
                <div className="card-header">
                  <div className="d-flex flex-colounm align-items-center  row-gap-3">
                    <div className="d-flex align-items-center justify-content-center avatar avatar-xl border border-dashed me-2 flex-shrink-0 text-dark">
                      {account?.avatar ? (
                        <img src={account.avatar} className="img-fluid rounded-circle" alt={name} />
                      ) : (
                        <span className="fw-semibold fs-24">{name.slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="overflow-hidden">
                      <span
                        className={`badge badge-soft-${inactive ? "danger" : "success"} d-inline-flex align-items-center mb-1`}
                      >
                        <i className="ti ti-circle-filled fs-5 me-1"></i>
                        {inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}
                      </span>
                      <h5 className="mb-1 text-truncate">{name}</h5>
                      <p className="text-primary">{profile.speciality || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Basic Information */}
                <div className="card-body">
                  <h5 className="mb-3">{t("common.ui.informationsGenerales")}</h5>
                  <dl className="row mb-0">
                    <dt className="col-6 fw-medium text-dark mb-3">{t("common.fields.speciality")}</dt>
                    <dd className="col-6 mb-3">{profile.speciality || "-"}</dd>
                    <dt className="col-6 fw-medium text-dark mb-3">{t("common.fields.department")}</dt>
                    <dd className="col-6 mb-3">{department?.name || "-"}</dd>
                    <dt className="col-6 fw-medium text-dark mb-3">{t("common.ui.statutCompte")}</dt>
                    <dd className="col-6 mb-3">{inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}</dd>
                    <dt className="col-6 fw-medium text-dark mb-3">{t("common.crud.enrolledOn")}</dt>
                    <dd className="col-6 mb-3">{formatDate(profile.created_at)}</dd>
                    <dt className="col-6 fw-medium text-dark mb-3">{t("pages.teacherDetail.lastLogin")}</dt>
                    <dd className="col-6 mb-3">{formatDateTime(account?.last_login)}</dd>
                  </dl>
                  <hr />
                  <h5 className="mb-3">{t("common.ui.coordonnees")}</h5>
                  <div className="d-flex align-items-center mb-3">
                    <span className="d-flex flex-column align-items-center justify-content-center avatar avatar-md bg-light rounded me-2 flex-shrink-0 text-default">
                      <i className="ti ti-phone"></i>
                    </span>
                    <div>
                      <span className="text-dark fw-medium mb-1">{t("common.fields.phone")}</span>
                      <p>{account?.phone || "-"}</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="d-flex flex-column align-items-center justify-content-center avatar avatar-md bg-light rounded me-2 flex-shrink-0 text-default">
                      <i className="ti ti-mail"></i>
                    </span>
                    <div>
                      <span className="text-dark fw-medium mb-1">{t("common.ui.adresseEMail")}</span>
                      <p>{account?.email || "-"}</p>
                    </div>
                  </div>
                </div>
                {/* /Basic Information */}
              </div>
            </div>
            {/* /Teacher Information */}

            <div className="col-xxl-8 col-xl-7">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">{t("pages.teacherDetail.teachingActivity")}</h5>
                </div>
                <div className="card-body">
                  {activityError && <div className="alert alert-warning">{activityError}</div>}

                  <h6 className="mb-3">{t("pages.teacherDetail.createdExams")}</h6>
                  {activityLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">{t("common.crud.loading")}</span>
                      </div>
                    </div>
                  ) : exams.length > 0 ? (
                    <div className="list-group list-group-flush mb-4">
                      {exams.slice(0, 5).map((exam) => (
                        <div key={exam.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <div>
                            <p className="mb-0 fw-medium">{exam.title}</p>
                            <small className="text-muted">{subjectLabel(exam.subject_id)}</small>
                          </div>
                          <span className={`badge badge-soft-${examStatusBadge[exam.status] || "secondary"}`}>
                            {examStatusLabels[exam.status] || exam.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t("pages.teacherDetail.noExamsCreated")}</p>
                  )}

                  <hr />

                  <h6 className="mb-3 mt-3">{t("pages.teacherDetail.scheduledSessions")}</h6>
                  {activityLoading ? null : examSessions.length > 0 ? (
                    <div className="table-responsive mb-2">
                      <table className="table table-sm mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>{t("common.ui.examen")}</th>
                            <th>{t("common.ui.debut")}</th>
                            <th>{t("common.ui.fin")}</th>
                            <th>{t("common.crud.statusColumn")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {examSessions.slice(0, 5).map((session) => (
                            <tr key={session.id}>
                              <td>{examLabel(session.exam_id)}</td>
                              <td>{formatDateTime(session.start_at)}</td>
                              <td>{formatDateTime(session.end_at)}</td>
                              <td>
                                <span className={`badge badge-soft-${sessionStatusBadge[session.status] || "secondary"}`}>
                                  {sessionStatusLabels[session.status] || session.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">{t("pages.teacherDetail.noScheduledSessions")}</p>
                  )}

                  <hr />

                  <h6 className="mb-3 mt-3">{t("menu.statistics")}</h6>
                  <div className="row">
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-clipboard-text text-primary fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("pages.teacherDetail.createdExams")}</p>
                          <h5 className="mb-0">{activityStats.totalExams}</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-circle-check text-success fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("common.ui.examensPublies")}</p>
                          <h5 className="mb-0">{activityStats.publishedExams}</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-info-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-calendar-event text-info fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("pages.teacherDetail.scheduledSessions")}</p>
                          <h5 className="mb-0">{activityStats.totalSessions}</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-warning-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-broadcast text-warning fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("common.ui.sessionsCours")}</p>
                          <h5 className="mb-0">{activityStats.ongoingSessions}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDetail;
